// Loads only the breakpoint-appropriate source so uploading both a desktop and a
// mobile video never downloads both; autoplay is gated on prefers-reduced-motion.
if (!customElements.get("video-hero")) {
  class VideoHero extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.iframe = null;
      this.playButton = null;
      this.pauseButton = null;
      this.currentSrc = null;
      this.mobileQuery = null;
      this.motionQuery = null;
      this.isPlaying = false;
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onPlayClick = this.onPlayClick.bind(this);
      this.onPauseClick = this.onPauseClick.bind(this);
      this.onVideoPlay = this.onVideoPlay.bind(this);
      this.onVideoPause = this.onVideoPause.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector("video[data-video-hero]");
      this.iframe = this.querySelector(".video-hero__iframe");
      this.playButton = this.querySelector("[data-video-hero-play]");
      this.pauseButton = this.querySelector("[data-video-hero-pause]");

      if (this.playButton) {
        this.playButton.addEventListener("click", this.onPlayClick);
        this.pauseButton?.addEventListener("click", this.onPauseClick);
      }

      if (this.video) {
        this.mobileQuery = window.matchMedia("(max-width: 749px)");
        this.motionQuery = window.matchMedia("(prefers-reduced-motion: no-preference)");

        this.selectSource();
        this.mobileQuery.addEventListener("change", this.onBreakpointChange);

        this.video.addEventListener("play", this.onVideoPlay);
        this.video.addEventListener("pause", this.onVideoPause);
        this.isPlaying = !this.video.paused;
      }

      this.updateControls();
    }

    disconnectedCallback() {
      this.mobileQuery?.removeEventListener("change", this.onBreakpointChange);
      this.playButton?.removeEventListener("click", this.onPlayClick);
      this.pauseButton?.removeEventListener("click", this.onPauseClick);
      this.video?.removeEventListener("play", this.onVideoPlay);
      this.video?.removeEventListener("pause", this.onVideoPause);
    }

    sourceForViewport() {
      const mobile = this.video.dataset.videoMobile;
      const desktop = this.video.dataset.videoDesktop;
      if (this.mobileQuery.matches && mobile) return mobile;
      return desktop || mobile || null;
    }

    selectSource() {
      const src = this.sourceForViewport();
      if (!src || src === this.currentSrc) return;
      this.currentSrc = src;
      this.video.src = src;
      this.video.load();
      this.maybePlay();
    }

    maybePlay() {
      if (this.video.dataset.autoplay !== "true") return;
      if (!this.motionQuery.matches) return;
      this.video.play().catch(() => {});
    }

    onBreakpointChange() {
      this.selectSource();
    }

    onPlayClick(event) {
      event.preventDefault();
      this.playMedia();
    }

    onPauseClick(event) {
      event.preventDefault();
      this.pauseMedia();
    }

    onVideoPlay() {
      this.isPlaying = true;
      this.updateControls();
    }

    onVideoPause() {
      this.isPlaying = false;
      this.updateControls();
    }

    updateControls() {
      if (!this.playButton && !this.pauseButton) return;
      this.dataset.state = this.isPlaying ? "playing" : "paused";
    }

    playMedia() {
      if (this.video) {
        if (!this.video.src && this.currentSrc) {
          this.video.src = this.currentSrc;
          this.video.load();
        }
        this.video.play().catch(() => {});
        return;
      }

      if (this.iframe) {
        const src = this.iframe.src || "";
        if (src.includes("youtube.com") || src.includes("youtube-nocookie.com")) {
          this.iframe.contentWindow?.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            "*",
          );
        } else if (src.includes("vimeo.com")) {
          this.iframe.contentWindow?.postMessage('{"method":"play"}', "*");
        }
        this.isPlaying = true;
        this.updateControls();
      }
    }

    pauseMedia() {
      if (this.video) {
        this.video.pause();
        return;
      }

      if (this.iframe) {
        const src = this.iframe.src || "";
        if (src.includes("youtube.com") || src.includes("youtube-nocookie.com")) {
          this.iframe.contentWindow?.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            "*",
          );
        } else if (src.includes("vimeo.com")) {
          this.iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
        }
        this.isPlaying = false;
        this.updateControls();
      }
    }
  }

  customElements.define("video-hero", VideoHero);
}
